import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Search, Plus, Users, User, Bell, UserPlus } from 'lucide-react';
import { useChats } from '@/hooks/mensag/useChats';
import { useFindUser } from '@/hooks/mensag/useNIP17';
import { useContactRequests, useSendContactRequest, useAcceptedContacts } from '@/hooks/mensag/useContacts';
import { ChatListItem } from '@/components/mensag/ChatListItem';
import { ContactRequests } from '@/components/mensag/ContactRequests';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export default function ChatsPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { data: chats, isLoading } = useChats();
  const [searchQuery, setSearchQuery] = useState('');
  const [addUserQuery, setAddUserQuery] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isContactRequestsOpen, setIsContactRequestsOpen] = useState(false);

  const { data: foundUser, isLoading: isSearchingUser } = useFindUser(addUserQuery);
  const { data: contactRequests } = useContactRequests();
  const { data: acceptedContacts } = useAcceptedContacts();
  const sendContactRequestMutation = useSendContactRequest();

  // Filter chats based on search query
  const filteredChats = chats?.filter(chat =>
    chat.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleSendContactRequest = async () => {
    if (!foundUser || !addUserQuery.trim()) return;

    try {
      await sendContactRequestMutation.mutateAsync({
        toNickname: addUserQuery.trim(),
        toPubkey: foundUser.pubkey,
        message: contactMessage.trim() || undefined
      });

      // Close dialog and reset
      setIsAddUserDialogOpen(false);
      setAddUserQuery('');
      setContactMessage('');
    } catch (error) {
      console.error('Error sending contact request:', error);
    }
  };

  // Count pending requests received
  const pendingRequestsCount = contactRequests?.filter(r => r.direction === 'received' && r.status === 'pending').length || 0;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
            Chats
          </h2>
          <div className="flex items-center space-x-2">
            {/* Contact Requests Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsContactRequestsOpen(true)}
              className="relative p-2"
            >
              <Bell className="h-4 w-4" />
              {pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                </span>
              )}
            </Button>

            {/* Add Contact Dialog */}
            <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Añadir Contacto</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Añadir Contacto</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Input
                      placeholder="Ingresa un nickname (ej: usuario123)"
                      value={addUserQuery}
                      onChange={(e) => setAddUserQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendContactRequest()}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Busca usuarios de MensaG por su nickname único
                    </p>
                  </div>

                  {isSearchingUser ? (
                    <div className="flex items-center space-x-3 p-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="text-sm text-gray-500">Buscando usuario...</span>
                    </div>
                  ) : foundUser ? (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={foundUser.profile?.picture} />
                            <AvatarFallback>
                              {foundUser.profile?.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-green-500" />
                              <span className="font-medium">
                                {foundUser.profile?.name || foundUser.profile?.display_name}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                MensaG
                              </Badge>
                            </div>
                            {foundUser.profile?.about && (
                              <p className="text-sm text-gray-500 mt-1">{foundUser.profile.about}</p>
                            )}
                          </div>
                        </div>

                        {/* Message field for invitation */}
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium">Mensaje de invitación (opcional)</label>
                          <Textarea
                            placeholder="¡Hola! Me gustaría agregarte como contacto..."
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                            maxLength={200}
                            rows={3}
                          />
                          <p className="text-xs text-gray-500">{contactMessage.length}/200</p>
                        </div>

                        <Button
                          onClick={handleSendContactRequest}
                          disabled={sendContactRequestMutation.isPending}
                          className="w-full mt-4"
                        >
                          {sendContactRequestMutation.isPending ? (
                            <div className="flex items-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Enviando solicitud...</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <UserPlus className="h-4 w-4" />
                              <span>Enviar solicitud de contacto</span>
                            </div>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : addUserQuery.trim() !== '' && !isSearchingUser ? (
                    <div className="text-center p-4 text-gray-500">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Usuario no encontrado</p>
                      <p className="text-xs">Verifica el nickname e intenta de nuevo</p>
                    </div>
                  ) : null}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 px-4 md:px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar conversaciones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-gray-50 dark:bg-gray-700 h-9 text-sm"
          />
        </div>
      </div>

      {/* Chats List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
              <MessageCircle className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchQuery ? 'No se encontraron conversaciones' : 'No tienes conversaciones'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
              {searchQuery
                ? 'Intenta con otro término de búsqueda'
                : 'Comienza una nueva conversación agregando un contacto por su nickname'
              }
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setIsAddUserDialogOpen(true)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar contacto
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                onClick={() => navigate(`/chat/${chat.contactId}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3">
        <div className="flex justify-around">
          <Button variant="ghost" size="sm" className="flex-1 md:flex-none">
            <MessageCircle className="h-4 w-4 mr-2" />
            <span className="hidden md:inline">Todos</span>
          </Button>
          <Button variant="ghost" size="sm" className="flex-1 md:flex-none">
            <span className="text-sm font-medium">No leídos</span>
            {filteredChats.length > 0 && filteredChats.some(chat => (chat.unreadCount || 0) > 0) && (
              <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {filteredChats.reduce((sum, chat) => sum + (chat?.unreadCount || 0), 0)}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Contact Requests Dialog */}
      <ContactRequests
        isOpen={isContactRequestsOpen}
        onOpenChange={setIsContactRequestsOpen}
      />
    </div>
  );
}